import { autoinject, bindable, computedFrom, ObserverLocator } from 'aurelia-framework';
import {
	validateTrigger,
	ValidationController,
	ValidationControllerFactory,
	ValidationRules
} from 'aurelia-validation';
import { sortBy } from 'lodash';
import {
	AccountingDimensionService,
	AccountingDimensionValue,
	IAccountingDimensionValueAM
} from './accounting-dimension-api';

@autoinject
export class AccountingDimensionValues {
	@bindable dimensionId: string;
	@bindable values: AccountingDimensionValue[];
	@bindable refreshValues: () => void;

	addTabId = 'add';
	importTabId = 'import';
	newValue = new AccountingDimensionValue({ removable: true });
	newValueIdentifierHasFocus: boolean;
	valueImportText: string;
	validationErrors: ValidationError[];

	private validationController: ValidationController;

	constructor(
		private dimensionService: AccountingDimensionService,
		private observerLocator: ObserverLocator,
		private validationControllerFactory: ValidationControllerFactory) {

		this.validationController = validationControllerFactory.createForCurrentScope();
		this.validationController.validateTrigger = validateTrigger.manual;
		this.attachValidationRules(this.newValue);
	}

	attached() {
		this.values.forEach(d => d.update = this.updateValue.bind(this));
	}

	updateValue(value: AccountingDimensionValue) {
		this.values = sortBy(this.values, v => v.identifier);
		this.dimensionService.updateValue(value);
	}

	attachValidationRules(value: AccountingDimensionValue) {
		ValidationRules
			.ensure((v: AccountingDimensionValue) => v.identifier)
			.required()
				.withMessageKey('AccountingDimensionValue_ValidationError_IdentifierIsRequired')
			.satisfies((identifier) => this.validateDimensionValueIdentifierFormat(identifier, null))
				.withMessageKey('AccountingDimensions_ValidationError_IdentifierFormat')
			.satisfies((identifier) => this.validateUniqueIdentifier(identifier, null))
				.withMessageKey('AccountingDimensionValue_ValidationError_IdentifierMustBeUnique')
			.on(value);
	}

	@computedFrom('dimensionId')
	get tabId() {
		return `dimension-${this.dimensionId}`;
	}

	async addValue() {
		const result = await this.validationController.validate({ object: this.newValue });

		if (!result.valid) {
			return;
		}

		const valueAM = await this.dimensionService.addValue(this.dimensionId, this.newValue);
		this.values = sortBy(this.values.concat(AccountingDimensionValue.fromAM(valueAM)), this.valueSort);
		this.newValue = new AccountingDimensionValue({ removable: true });
		this.newValueIdentifierHasFocus = true;
		this.refreshValues();
		this.attachValidationRules(this.newValue);
	}

	removeValue(value: AccountingDimensionValue) {
		this.dimensionService.removeValue(value);

		this.values = this.values.filter(v => v.identifier !== value.identifier);
		this.refreshValues();
		value.confirmRemove = false;
	}

	refreshContainer() {
		const dirtyChecker = (this.observerLocator as any).dirtyChecker;
		dirtyChecker.check();
		this.refreshValues();
	}

	private dimensionValueIdentifierRegex: RegExp = /^[a-zäö0-9_\/+-]+$/i
	validateDimensionValueIdentifierFormat(newValue: string, oldValue?: string) {
		return this.dimensionValueIdentifierRegex.test(newValue);
	}
	
	validateUniqueIdentifier(newValue: string, oldValue?: string) {
		return newValue === oldValue || !this.values.some(v => v.identifier === newValue);
	}

	async importValues() {
		let values = this.valueImportText.split('\n')
			.filter(row => row.length > 0)
			.map(row => {
				const fields = row.split('\t');
				return {
					identifier: fields[0],
					name: fields.length > 1 ? fields[1] : null
				};
			});

		values = this.filterDuplicateValues(values);

		this.validationErrors = [];
		this.validationErrors = this.validateValues(values);
		if (this.validationErrors.length > 0) {
			this.refreshValues();
			return;
		}

		const valueAMs = await this.dimensionService.importValues(this.dimensionId, values);
		this.values = sortBy(
			this.values.concat(valueAMs.map(valueAM => AccountingDimensionValue.fromAM(valueAM))),
			this.valueSort);
		this.valueImportText = '';
		this.refreshValues();
	}

	// Filters values that are previously saved or occur multiple times in the imported data
	private filterDuplicateValues(values: IAccountingDimensionValueAM[]) : IAccountingDimensionValueAM[] {
		const unique = (str: string, vals: string[]) => vals.indexOf(str) === -1;
		
		return  values.filter((value, index) => {
			return this.validateUniqueIdentifier(value.identifier) && unique(value.identifier, values.map(v => v.identifier).slice(0, index));
		});
	}

	private validateValues(values: IAccountingDimensionValueAM[]) {		

		const errors = new Array<ValidationError>();
		
		values.forEach((value, index) => {
			const error = new ValidationError({ rowNumber: index + 1, errors: [] });

			if(!value.identifier) error.errors.push('AccountingDimensionValue_ValidationError_IdentifierIsRequired');

			if(error.errors.length > 0) errors.push(error);
		});

		return  errors;
	}

	private valueSort = (v : AccountingDimensionValue) => v.identifier;
}

export class ValidationError {
	rowNumber: number;
	errors: string[];

	constructor(init: Partial<ValidationError>) {
		Object.assign(this, init);
	}
}
